import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	BarChart3,
	TrendingUp,
	Package,
	FileText,
	Download,
} from 'lucide-react'

import { PageHeader } from '../../components/ui/PageHeader'
import { Card, CardContent } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useDashboardStats, useSalesTrend } from '../../hooks/useDashboardStats'

export default function ReportsPage() {
	const { t, i18n } = useTranslation()

	const [chartPeriod, setChartPeriod] = useState<'day' | 'week' | 'month' | 'year'>('week')

	const {
		data: stats,
		isLoading: isLoadingStats,
	} = useDashboardStats()

	const {
		data: salesTrend,
		isLoading: isLoadingSalesTrend,
	} = useSalesTrend(chartPeriod)

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
			style: 'currency',
			currency: 'EGP',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value)
	}

	if (isLoadingStats) {
		return (
			<div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
				<LoadingSpinner size="lg" />
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title={t('reports.title', 'Reports')}
				subtitle={t('reports.subtitle', 'View analytics and reports')}
				actions={
					<Button variant="outline" leftIcon={<Download className="h-4 w-4" />}>
						{t('reports.export', 'Export Report')}
					</Button>
				}
			/>

			{/* Summary Cards */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('reports.totalProducts', 'Total Products')}
								</p>
								<p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
									{stats?.totalProducts || 0}
								</p>
							</div>
							<Package className="h-8 w-8 text-blue-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('reports.lowStockItems', 'Low Stock Items')}
								</p>
								<p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
									{stats?.lowStockCount || 0}
								</p>
							</div>
							<BarChart3 className="h-8 w-8 text-yellow-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('reports.totalValue', 'Total Value')}
								</p>
								<p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
									{formatCurrency(stats?.monthlyRevenue || 0)}
								</p>
							</div>
							<TrendingUp className="h-8 w-8 text-green-500" />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t('reports.todaySales', 'Today Sales')}
								</p>
								<p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
									{formatCurrency(stats?.todaySales?.amount || 0)}
								</p>
							</div>
							<FileText className="h-8 w-8 text-purple-500" />
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Sales Trend Chart */}
			<Card>
				<CardContent className="p-6">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 sm:mb-0">
							{t('reports.salesTrend', 'Sales Trend')}
						</h3>
						<div className="flex gap-2">
							<select
								value={chartPeriod}
								onChange={(e) => setChartPeriod(e.target.value as any)}
								className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm"
							>
								<option value="day">{t('reports.period.day', 'Day')}</option>
								<option value="week">{t('reports.period.week', 'Week')}</option>
								<option value="month">{t('reports.period.month', 'Month')}</option>
								<option value="year">{t('reports.period.year', 'Year')}</option>
							</select>
						</div>
					</div>
					{isLoadingSalesTrend ? (
						<div className="flex items-center justify-center h-64">
							<LoadingSpinner />
						</div>
					) : salesTrend && salesTrend.length > 0 ? (
						<div className="space-y-4">
							<p className="text-sm text-gray-500 dark:text-gray-400">
								{t('reports.salesTrendDescription', 'Sales trend data will be displayed here.')}
							</p>
						</div>
					) : (
						<div className="flex items-center justify-center h-64 text-gray-500">
							{t('reports.noData', 'No data available')}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Additional Reports Section */}
			<div>
				<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
					{t('reports.availableReports', 'Available Reports')}
				</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					<Card className="hover:shadow-lg transition-shadow cursor-pointer">
						<CardContent className="p-6">
							<div className="flex items-center gap-3 mb-4">
								<Package className="h-6 w-6 text-blue-500" />
								<h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
									{t('reports.inventoryReport', 'Inventory Report')}
								</h4>
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
								{t('reports.inventoryReportDescription', 'View detailed inventory levels and stock movements.')}
							</p>
							<Button variant="outline" size="sm" onClick={() => window.location.href = '/inventory'}>
								{t('reports.viewReport', 'View Report')}
							</Button>
						</CardContent>
					</Card>

					<Card className="hover:shadow-lg transition-shadow cursor-pointer">
						<CardContent className="p-6">
							<div className="flex items-center gap-3 mb-4">
								<TrendingUp className="h-6 w-6 text-green-500" />
								<h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
									{t('reports.transactionReport', 'Transaction Report')}
								</h4>
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
								{t('reports.transactionReportDescription', 'Analyze sales, purchases, and transfers.')}
							</p>
							<Button variant="outline" size="sm" onClick={() => window.location.href = '/invoices'}>
								{t('reports.viewReport', 'View Report')}
							</Button>
						</CardContent>
					</Card>

					<Card className="hover:shadow-lg transition-shadow cursor-pointer">
						<CardContent className="p-6">
							<div className="flex items-center gap-3 mb-4">
								<FileText className="h-6 w-6 text-purple-500" />
								<h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
									{t('reports.inventoryMovement', 'Inventory Movement')}
								</h4>
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
								{t('reports.inventoryMovementDescription', 'Track stock movements, transfers, and adjustments.')}
							</p>
							<Button variant="outline" size="sm">
								{t('reports.viewReport', 'View Report')}
							</Button>
						</CardContent>
					</Card>

					<Card className="hover:shadow-lg transition-shadow cursor-pointer">
						<CardContent className="p-6">
							<div className="flex items-center gap-3 mb-4">
								<BarChart3 className="h-6 w-6 text-orange-500" />
								<h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
									{t('reports.productPerformance', 'Product Performance')}
								</h4>
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
								{t('reports.productPerformanceDescription', 'Analyze top-selling products and performance metrics.')}
							</p>
							<Button variant="outline" size="sm">
								{t('reports.viewReport', 'View Report')}
							</Button>
						</CardContent>
					</Card>

					<Card className="hover:shadow-lg transition-shadow cursor-pointer">
						<CardContent className="p-6">
							<div className="flex items-center gap-3 mb-4">
								<FileText className="h-6 w-6 text-indigo-500" />
								<h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
									{t('reports.partnerReport', 'Partner Report')}
								</h4>
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
								{t('reports.partnerReportDescription', 'View supplier and customer transaction summaries.')}
							</p>
							<Button variant="outline" size="sm">
								{t('reports.viewReport', 'View Report')}
							</Button>
						</CardContent>
					</Card>

					<Card className="hover:shadow-lg transition-shadow cursor-pointer">
						<CardContent className="p-6">
							<div className="flex items-center gap-3 mb-4">
								<BarChart3 className="h-6 w-6 text-red-500" />
								<h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
									{t('reports.customReport', 'Custom Report')}
								</h4>
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
								{t('reports.customReportDescription', 'Create custom reports with date ranges and filters.')}
							</p>
							<Button variant="outline" size="sm">
								{t('reports.createReport', 'Create Report')}
							</Button>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	)
}
